import type { Column, Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { PERIOD_LABELS, formatDayLong, type Period, type Range } from "./dates";
import type { Entry } from "./db";

export const DEFAULT_ACCENT = "#1e40af";
const MUTED = "#64748b";
const BORDER = "#e2e8f0";

export function groupByDate(entries: Entry[]) {
  const groups = new Map<string, Entry[]>();
  for (const e of entries) {
    const list = groups.get(e.date) ?? [];
    list.push(e);
    groups.set(e.date, list);
  }
  return [...groups.entries()];
}

export function groupByClient(entries: Entry[]) {
  const groups = new Map<number, { id: number; name: string; entries: Entry[] }>();
  for (const e of entries) {
    const g = groups.get(e.client_id) ?? { id: e.client_id, name: e.client_name, entries: [] };
    g.entries.push(e);
    groups.set(e.client_id, g);
  }
  return [...groups.values()].sort((a, b) => b.entries.length - a.entries.length || a.name.localeCompare(b.name, "tr"));
}

// Tek müşteriye özel raporda o müşterinin rengi ve logosu kullanılır
export type Brand = { color?: string | null; logo?: string | null /* PNG data URL */ };

export function buildDoc(period: Period, range: Range, entries: Entry[], brand: Brand = {}): TDocumentDefinitions {
  const accent = brand.color || DEFAULT_ACCENT;
  const days = groupByDate(entries);
  const created = new Intl.DateTimeFormat("tr-TR", { dateStyle: "long", timeStyle: "short" }).format(new Date());

  // Müşteriye gönderilecek: müşteri/proje adı PDF'te hiç yer almaz
  const summary: Content = {
    table: {
      widths: ["*", "*"],
      body: [
        [
          { text: "Toplam kayıt", style: "statLabel" },
          { text: "Çalışılan gün", style: "statLabel" },
        ],
        [
          { text: String(entries.length), style: "statValue" },
          { text: String(days.length), style: "statValue" },
        ],
      ],
    },
    layout: "noBorders",
    margin: [0, 0, 0, 16],
  };

  const dayBlocks: Content[] = days.flatMap(([date, list]) => [
    { text: formatDayLong(date), style: "h2" },
    {
      table: {
        headerRows: 1,
        dontBreakRows: true,
        widths: [150, "*"],
        body: [
          [
            { text: "Başlık", style: "th" },
            { text: "Açıklama", style: "th" },
          ],
          ...list.map(e => [
            { text: e.title, bold: true },
            { text: e.description || "—", color: e.description ? "#1e293b" : MUTED },
          ]),
        ],
      },
      layout: tableLayout,
      margin: [0, 0, 0, 14],
    },
  ]);

  const header: Column[] = [
    {
      width: "*",
      stack: [
        { text: `${PERIOD_LABELS[period].toLocaleUpperCase("tr-TR")} ÇALIŞMA RAPORU`, style: "eyebrow" },
        { text: range.label, style: "h1" },
      ],
    },
  ];
  if (brand.logo) header.push({ width: "auto", stack: [{ image: brand.logo, fit: [140, 48] }] });

  return {
    pageSize: "A4",
    pageMargins: [40, 48, 40, 48],
    info: { title: `${PERIOD_LABELS[period]} Rapor – ${range.label}` },
    defaultStyle: { font: "Roboto", fontSize: 10, color: "#1e293b", lineHeight: 1.2 },
    footer: (page, pages) => ({
      columns: [
        { text: `Oluşturulma: ${created}`, color: MUTED, fontSize: 8 },
        { text: `${page} / ${pages}`, alignment: "right", color: MUTED, fontSize: 8 },
      ],
      margin: [40, 16, 40, 0],
    }),
    content: [
      { columns: header, columnGap: 16 },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.5, lineColor: accent }],
        margin: [0, 6, 0, 16],
      },
      summary,
      ...(entries.length === 0
        ? [{ text: "Bu dönemde kayıt bulunmuyor.", color: MUTED, italics: true } as Content]
        : dayBlocks),
    ],
    styles: {
      eyebrow: { fontSize: 9, bold: true, color: accent, characterSpacing: 1 },
      h1: { fontSize: 20, bold: true, margin: [0, 2, 0, 0] },
      h2: { fontSize: 12, bold: true, margin: [0, 0, 0, 6] },
      th: { bold: true, fontSize: 9, color: MUTED },
      statLabel: { fontSize: 9, color: MUTED },
      statValue: { fontSize: 18, bold: true, color: accent },
    },
  };
}

const tableLayout = {
  hLineWidth: (i: number) => (i === 0 ? 0 : 0.6),
  vLineWidth: () => 0,
  hLineColor: () => BORDER,
  fillColor: (row: number) => (row === 0 ? "#f1f5f9" : null),
  paddingTop: () => 6,
  paddingBottom: () => 6,
  paddingLeft: () => 6,
  paddingRight: () => 6,
};

// Logoyu PNG data URL'e çevirir (pdfmake WEBP/SVG okuyamaz). Yüklenemezse logosuz devam edilir.
async function logoToPng(url: string): Promise<string | null> {
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    // SVG'lerin doğal boyutu olmayabilir; keskin görünsün diye büyük çiz
    const w = img.naturalWidth || 600;
    const h = img.naturalHeight || 200;
    const scale = Math.min(1, 1200 / Math.max(w, h)) * (url.includes("svg") ? 2 : 1);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

export async function downloadReportPdf(
  period: Period,
  range: Range,
  entries: Entry[],
  brand: { color?: string | null; logoUrl?: string | null } = {},
) {
  // pdfmake ve fontları büyük; sadece PDF istendiğinde yüklenir
  const [{ default: pdfMake }, { default: vfs }] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts"),
  ]);
  pdfMake.addVirtualFileSystem(vfs);
  const suffix = period === "daily" ? range.from : `${range.from}_${range.to}`;
  const logo = brand.logoUrl ? await logoToPng(brand.logoUrl) : null;
  const doc = buildDoc(period, range, entries, { color: brand.color, logo });
  await pdfMake.createPdf(doc).download(`rapor-${period}-${suffix}.pdf`);
}
